import React, { useState } from 'react';
import SEOHead from '@components/marketing/SEOHead';

const FACTS = [
  ['Setup', 'Two weeks from a signed scope'],
  ['Training', 'One session per role, recorded'],
  ['Support', 'Email and WhatsApp through the season'],
];

const EXPECT = [
  'We set up a working tenant with your sports, venues and categories before the call, so you are looking at your own competition rather than a sample one.',
  'We walk the four accounts in turn — the league office, a coordinator, a scorer on a phone and a player — because each one sees something different.',
  'We put a live board on screen and score a match into it, which is the part most people want to see for themselves.',
  'You keep the tenant afterwards to poke at in your own time.',
];

const DemoPage = () => {
  const [notification, setNotification] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', organization: '', message: '',
  });

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const { name, email, phone, organization, message } = formData;

    if (!name || !email || !organization || !message) {
      showNotification('Fill in your name, email, organisation and message.', 'error');
      return;
    }
    if (!/^[A-Za-z ]{3,}$/.test(name)) {
      showNotification('Enter your full name using letters only.', 'error');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showNotification('That email address does not look right.', 'error');
      return;
    }
    if (phone && !/^[0-9]{10,13}$/.test(phone.replace(/\D/g, ''))) {
      showNotification('Enter a phone number of 10 to 13 digits.', 'error');
      return;
    }
    if (message.length > 200) {
      showNotification('Keep the message under 200 characters.', 'error');
      return;
    }

    setIsSubmitting(true);
    setTimeout(() => {
      setFormData({ name: '', email: '', phone: '', organization: '', message: '' });
      showNotification('Thanks — we have your message and will be in touch.', 'success');
      setIsSubmitting(false);
    }, 1200);
  };

  return (
    <>
      <SEOHead
        title="Book a demo"
        description="Tell us the sports, venues and roughly how many people. We set up a working SportsTMS tenant with your own competition in it before the call."
      />

      {notification && (
        <div className={`stms-toast stms-toast--${notification.type}`} role="status">
          {notification.message}
        </div>
      )}

      <section className="stms-sec stms-sec--ink stms-sec--first">
        <div className="stms-wrap stms-split">
          <div className="stms-split__say">
            <h1>Bring us your season</h1>
            <p>
              Tell us the sports, the venues and roughly how many people. We will set up a
              working tenant with your own data so you can see the real thing, not a
              slideshow.
            </p>
            <ul className="stms-contact__facts">
              {FACTS.map(([k, v]) => (
                <li key={k}><b>{k}</b><span>{v}</span></li>
              ))}
            </ul>
          </div>

          <form className="stms-form" onSubmit={handleSubmit} noValidate>
            <div className="stms-field">
              <label htmlFor="f-name">Your name</label>
              <input id="f-name" name="name" value={formData.name} onChange={handleChange} autoComplete="name" required />
            </div>
            <div className="stms-field">
              <label htmlFor="f-org">Association or company</label>
              <input id="f-org" name="organization" value={formData.organization} onChange={handleChange} autoComplete="organization" required />
            </div>
            <div className="stms-field">
              <label htmlFor="f-email">Email</label>
              <input id="f-email" name="email" type="email" value={formData.email} onChange={handleChange} autoComplete="email" required />
            </div>
            <div className="stms-field">
              <label htmlFor="f-phone">Phone <span className="stms-field__opt">optional</span></label>
              <input id="f-phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} autoComplete="tel" />
            </div>
            <div className="stms-field stms-field--wide">
              <label htmlFor="f-msg">What are you running?</label>
              <textarea id="f-msg" name="message" rows="4" maxLength="200" value={formData.message} onChange={handleChange} required />
              <p className="stms-field__count stms-num">{formData.message.length} / 200</p>
            </div>
            <button className="stms-btn stms-btn--solid stms-btn--lg" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending' : 'Send message'}
            </button>
          </form>
        </div>
      </section>

      <section className="stms-sec">
        <div className="stms-wrap">
          <div className="stms-lede"><h2>What the call looks like</h2></div>
          <ol className="stms-expect">
            {EXPECT.map((e, i) => (
              <li key={e}>
                <span className="stms-expect__n stms-num" aria-hidden="true">{i + 1}</span>
                <p>{e}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </>
  );
};

export default DemoPage;
